// A new opportunity needs its account's Employees and Annual Revenue. When the
// server refuses one for that, this asks for the missing values in a dialog,
// saves them to the account, and lets the create try again. Mounted once in AppShell.
import { RecordFormDialog, type RecordFormField } from '@olytic/foundation';
import { useEffect, useState } from 'react';

import { authenticatedFetch } from '../auth/authClient';
import { type AccountSizeNeeded, setAccountSizeAsker, updateRecord } from './writes';

interface Pending {
  need: AccountSizeNeeded;
  resolve: (saved: boolean) => void;
}

const FIELDS: Record<AccountSizeNeeded['missing'][number], RecordFormField> = {
  employee_count: { key: 'employee_count', label: 'Employees', required: true, editor: { kind: 'number', min: 0, step: 1 } },
  annual_revenue: { key: 'annual_revenue', label: 'Annual Revenue', required: true, editor: { kind: 'number', min: 0, step: 1 } },
};

export function AccountSizeHost(): JSX.Element | null {
  const [pending, setPending] = useState<Pending | null>(null);

  useEffect(() => {
    setAccountSizeAsker((need) => new Promise((resolve) => setPending({ need, resolve })));
    return () => setAccountSizeAsker(null);
  }, []);

  if (!pending) return null;
  const { need } = pending;
  const close = (saved: boolean) => {
    pending.resolve(saved);
    setPending(null);
  };

  return (
    <RecordFormDialog
      open
      onOpenChange={(open) => { if (!open) close(false); }}
      title={`Company details for ${need.name}`}
      fields={need.missing.map((key) => FIELDS[key])}
      submitLabel="Save and continue"
      onSubmit={async (values) => {
        const body: Record<string, number> = {};
        for (const key of need.missing) body[key] = Number(values[key]);
        await updateRecord(authenticatedFetch, 'accounts', need.id, body);
        close(true);
      }}
    />
  );
}
